// React
import { useRef } from "react"

// I18n
import { useTrans } from "@/i18n"

// Forms
import { Yup } from "@/lib/forms"
import { FormikProvider, useFormik, mapRestErrorsToFormik } from "@/lib/formik"
import { SelectButton, Select, FormGroup } from "@/components/form-controls"
import { FiChevronDown } from "@/lib/icons"
import {
	FormikError,
	FormikErrors,
	FormikInput,
	FormikLabel,
	FormikSubmitButton,
} from "@/components/form-controls/formik"
import { LabelSubtitle } from "@/components/form-controls/Label"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat, apiDateFormat } from "@/constants/constants"

// UI
import { StepFailedOverlay } from "@/pages/investor/pages/identity/_components/StepFailedOverlay"
import { StepCompletedOverlay } from "@/pages/investor/pages/identity/_components/StepCompletedOverlay"
import { Heading } from "@/components/Typography"
import { CardBody, CardFooter, CardWrapper } from "@/components/Card"

// Context
import { useToasts } from "@/context/useToasts"
import { useCurrentUserMangoPayStatus } from "@/context/user"

// GraphQL
import {
	KycVerificationDocumentType,
	RiskKycStateChoices,
	useUserKycStatusQuery,
} from "@/api/graphql"
import { usersKycDocumentsUploadCreate } from "@/api/rest/generated/users/users"
import {
	KycVerificationDocument,
	DocumentTypeEnum,
} from "@/api/rest/generated/@types"

// Tracking
import { captureMessage } from "@/lib/sentry"

/**
 * InvestorIdentityForm
 * @returns
 */
export const InvestorIdentityForm = () => {
	// Context
	const toasts = useToasts()
	const mangopayStatus = useCurrentUserMangoPayStatus()

	// Translations
	const t = useTrans("investor")

	// Query
	const { data, refetch } = useUserKycStatusQuery()

	// Form
	const formRef = useRef<HTMLFormElement>(null!)
	const form = useFormik({
		initialValues: {
			document: "",
			document_back: "",
			document_type: undefined as DocumentTypeEnum | undefined,
			expiry_date: "",
			full_first_name: "",
		},
		validationSchema: Yup.object().shape({
			expiry_date: Yup.string()
				.required(
					t(
						"investor:identity.form.field.expiry-date.error.required",
					),
				)
				.matches(
					/^([0-9]{2})-([0-9]{2})-([0-9]{4})$/, // DD-MM-YYYY
					"investor:identity.form.field.expiry-date.error.format",
				),
			full_first_name: Yup.string().required(
				t("investor:identity.form.field.first-name.error.required"),
			),
		}),
		onSubmit: async (values, helpers) => {
			helpers.setStatus("")
			try {
				const uuid = data?.me?.uuid
				const response = await usersKycDocumentsUploadCreate(uuid, {
					document: values.document,
					document_back: values.document_back,
					document_type: values.document_type as DocumentTypeEnum,
					expiry_date: DateTime.fromFormat(
						values.expiry_date,
						dateFormat,
					).toFormat(apiDateFormat),
					full_first_name: values.full_first_name,
				} as KycVerificationDocument)

				if (response.ok === true) {
					toasts.addToast({
						variant: "success",
						id: "kyc-submission-success",
						text: t("investor:identity.form.success"),
					})

					// Refetch data
					void refetch()

					// Scroll to top
					window.scrollTo(0, 0)
				} else {
					// 200 with { ok: false } — no field errors on the wire,
					// fall through to the generic toast + Sentry report.
					throw response
				}
			} catch (e) {
				// 4xx bodies are thrown by src/api/rest/api.ts as the parsed
				// JSON, shape { field: [msg] } — try to map to form fields.
				const mapped = mapRestErrorsToFormik(e, form)
				if (mapped) return

				captureMessage("KYC submission failed", {
					level: "error",
					extra: { error: e },
				})
				toasts.addToast({
					variant: "alert",
					id: "kyc-submission-error",
					text: t("investor:identity.form.error"),
				})
			}
		},
	})

	// When changing document_type
	async function onChangeDocumentType(type: DocumentTypeEnum) {
		form.resetForm()
		formRef.current.reset()
		void form.setFieldValue("document_type", type)
	}

	// Render
	return (
		<FormikProvider value={form}>
			<CardWrapper className="relative mt-4">
				{/** Show blocking overlay when mangopay status is false */}
				{mangopayStatus === false &&
				data?.me?.kyc?.state ===
					RiskKycStateChoices.VerificationRequested ? (
					<StepFailedOverlay
						title={t("investor:identity.blocked.title")}
						message={t("investor:identity.blocked.copy")}
					/>
				) : null}

				{/** Show blocking overlay when status is NOT requested */}
				{data?.me?.kyc?.state !== undefined &&
				data?.me?.kyc?.state !==
					RiskKycStateChoices.VerificationRequested ? (
					<StepCompletedOverlay
						state={data?.me?.kyc?.state}
						message={data?.me?.kyc?.human_readable_kyc_error}
						last_verified_on={data?.me?.kyc?.last_verified_on}
						documents={
							data?.me?.kyc?.documents?.filter(
								Boolean,
							) as KycVerificationDocumentType[]
						}
					/>
				) : null}

				{/* Form */}
				<form onSubmit={form.handleSubmit} ref={formRef}>
					<CardBody>
						<FormGroup
							heading={
								<>
									<Heading
										as="h2"
										styleAs="h5"
										className="mb-3"
									>
										{t(
											"investor:identity.photo-requirements.instructions",
										)}
									</Heading>
									<ul className="ml-4 list-disc">
										<li>
											{t(
												"investor:identity.photo-requirements.req.quality",
											)}
										</li>
										<li>
											{t(
												"investor:identity.photo-requirements.req.lighting",
											)}
										</li>
										<li>
											{t(
												"investor:identity.photo-requirements.req.visibility",
											)}
										</li>
										<li>
											{t(
												"investor:identity.photo-requirements.req.edges",
											)}
										</li>
										<li>
											{t(
												"investor:identity.photo-requirements.req.no-watermark",
											)}
										</li>
										<li>
											{t(
												"investor:identity.photo-requirements.req.back-side",
											)}
										</li>
										<li>
											{t(
												"investor:identity.photo-requirements.req.color",
											)}
										</li>
									</ul>
								</>
							}
						>
							<div className="space-y-6">
								<div className="relative flex space-x-6">
									{/** Example img */}
									<img
										className="align-center w-1/2"
										src={
											{
												default:
													"/assets/kyc/choose_id.jpg",
												driving_license:
													"/assets/kyc/driving_license_example.jpg",
												national_id_number:
													"/assets/kyc/national_id_example.jpg",
												passport:
													"/assets/kyc/passport_example.jpg",
												residence_permit:
													"/assets/kyc/residence_permit_example.jpg",
											}[
												form.values.document_type ??
													"default"
											]
										}
										alt={t(
											`investor:identity.form.field.document-type.example_alt.${form.values.document_type ?? "default"}`,
										)}
									/>

									{/** Document Type */}
									<div className="relative flex-col">
										<FormikLabel htmlFor="document_type">
											{t(
												"investor:identity.form.field.document-type.label",
											)}
											<LabelSubtitle>
												{t(
													"investor:identity.form.field.document-type.sublabel",
												)}
											</LabelSubtitle>
										</FormikLabel>
										<SelectButton className="w-full">
											{form.values.document_type ===
											undefined
												? t(
														"investor:identity.form.field.document-type.label",
													)
												: t(
														`investor:identity.form.field.document-type.options.${form.values.document_type}`,
													)}
											<FiChevronDown className={"ml-2"} />
											<Select
												id="document_type"
												name="document_type"
												className="absolute top-0 left-0 h-100 w-full cursor-pointer opacity-0"
												onChange={(event) =>
													void onChangeDocumentType(
														event.currentTarget
															.value as DocumentTypeEnum,
													)
												}
												value={
													form.values.document_type ??
													undefined
												}
											>
												{/** Default option */}
												<option
													disabled
													selected={
														form.values
															.document_type ===
														undefined
													}
												>
													{t(
														"investor:identity.form.field.document-type.label",
													)}
												</option>
												{Object.entries(
													DocumentTypeEnum,
												).map(([, value]) => (
													<option
														value={value}
														key={value}
													>
														{t(
															`investor:identity.form.field.document-type.options.${value}`,
														)}
													</option>
												))}
											</Select>
										</SelectButton>
									</div>
								</div>

								{form.values.document_type !== undefined && (
									<>
										{/** Document File Front */}
										<FormikLabel htmlFor="document">
											{t(
												"investor:identity.form.field.photo-front-side.label",
											)}
											<LabelSubtitle>
												{t(
													"investor:identity.form.field.photo.sublabel",
												)}
											</LabelSubtitle>
										</FormikLabel>
										<input
											type="file"
											name="document"
											onChange={(e) => {
												void form.setFieldValue(
													"document",
													e.target.files?.[0] as File,
												)
											}}
										/>
										<FormikError
											field="document"
											namespace="investor"
										/>

										{/** Document File Back */}
										{form.values.document_type !==
											DocumentTypeEnum.passport && (
											<>
												<FormikLabel htmlFor="document_back">
													{t(
														"investor:identity.form.field.photo-back-side.label",
													)}
													<LabelSubtitle>
														{t(
															"investor:identity.form.field.photo.sublabel",
														)}
													</LabelSubtitle>
												</FormikLabel>
												<input
													type="file"
													name="document_back"
													onChange={(event) => {
														void form.setFieldValue(
															"document_back",
															event.target
																.files?.[0] as File,
														)
													}}
												/>
												<FormikError
													field="document_back"
													namespace="investor"
												/>
											</>
										)}

										{/** First name */}
										<div>
											<FormikLabel htmlFor="full_first_name">
												{t(
													"investor:identity.form.field.first-name.label",
												)}
												<LabelSubtitle>
													{t(
														"investor:identity.form.field.first-name.sublabel",
													)}
												</LabelSubtitle>
											</FormikLabel>
											<FormikInput
												name="full_first_name"
												className="block w-full"
												classNameWrapper="lg:w-1/2"
												aria-label={t(
													"investor:identity.form.field.first-name.label",
												)}
											/>
											<FormikError
												field="full_first_name"
												namespace="investor"
											/>
										</div>

										{/** Expiry date */}
										<div>
											<FormikLabel htmlFor="expiry_date">
												{t(
													"investor:identity.form.field.expiry-date.label",
												)}
												<LabelSubtitle>
													{t(
														"investor:identity.form.field.expiry-date.sublabel",
													)}
												</LabelSubtitle>
											</FormikLabel>
											<FormikInput
												name="expiry_date"
												placeholder={t(
													"investor:identity.form.field.expiry-date.placeholder",
												)}
												className="block w-full"
												classNameWrapper="lg:w-1/2"
												aria-label={t(
													"investor:identity.form.field.expiry-date.label",
												)}
											/>
											<FormikError
												field="expiry_date"
												namespace="investor"
											/>
										</div>
									</>
								)}

								<div>
									<FormikErrors i18nNamespace="investor" />
								</div>
							</div>
						</FormGroup>
					</CardBody>
					<CardFooter className="text-right">
						<FormikSubmitButton
							disabled={
								data?.me?.kyc?.state ===
								RiskKycStateChoices.VerificationSuccess
							}
						>
							{t("investor:identity.form.button.submit")}
						</FormikSubmitButton>
					</CardFooter>
				</form>
			</CardWrapper>
		</FormikProvider>
	)
}
